import { useNavigate, Link } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { useAuthStore } from '../lib/auth'
import CosmicParallaxBg from '../components/CosmicParallaxBg'


const TestimonialCard = ({ quote, author, role }) => (
  <div className="card relative">
    <div className="text-cyan-500 text-6xl absolute top-4 left-6 opacity-20 font-serif">"</div>
    <p className="text-gray-300 mb-6 relative z-10 text-lg leading-relaxed">{quote}</p>
    <div className="flex items-center gap-4">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-700 flex items-center justify-center font-bold text-white text-sm">
        {author[0]}
      </div>
      <div>
        <div className="font-bold text-white">{author}</div>
        <div className="text-sm text-gray-500">{role}</div>
      </div>
    </div>
  </div>
)

export default function Landing() {
  const navigate = useNavigate()
  const { user } = useAuthStore()

  return (
    <div className="min-h-screen bg-[#0a0e17] relative overflow-hidden">

      {/* Hero Wrapper with Cosmic Background */}
      <CosmicParallaxBg>
        <div className="relative w-full min-h-screen flex flex-col">

          <div className="relative z-10 w-full">
            <Header />
          </div>

          {/* Hero Content */}
          <section className="container mx-auto px-4 pt-4 pb-8 max-w-5xl text-center relative z-10 flex-grow flex flex-col justify-center">
            <h1
              className="text-4xl md:text-7xl font-bold text-white leading-tight mb-4"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              Don't let scammers
              <br />
              <span className="text-white/60">steal your career.</span>
            </h1>

            <h2 className="text-lg md:text-2xl font-semibold text-cyan-400 mb-4">
              AI-Powered Job Scam Detection — Verify Recruiters in Seconds
            </h2>

            <p className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto mb-8">
              Instant job scam checker and fake recruiter detector. Paste any recruiter email,
              phone number, or job offer to get an AI-powered risk score. Free scan — no account required.
            </p>

            <div className="flex gap-4 justify-center mb-10">
              <Link to={user ? '/dashboard' : '/free-scan'} className="btn-secondary text-sm px-5 py-2.5 border border-white/20 rounded-full">
                Try Free Scan
              </Link>
              <Link to="/plans" className="btn-secondary text-sm px-5 py-2.5 border border-white/20 rounded-full">
                View Plans
              </Link>
            </div>

            {/* Product mockup */}
            <div className="mx-auto w-full max-w-lg text-left">
              {/* App chrome */}
              <div className="rounded-2xl border border-white/10 bg-[#0d1321] shadow-2xl overflow-hidden">
                {/* Title bar */}
                <div className="flex items-center gap-2 px-4 py-3 bg-white/5 border-b border-white/10">
                  <div className="w-3 h-3 rounded-full bg-red-500/60" />
                  <div className="w-3 h-3 rounded-full bg-amber-500/60" />
                  <div className="w-3 h-3 rounded-full bg-emerald-500/60" />
                  <span className="ml-3 text-xs text-gray-500 font-mono">scamview.ai/scan</span>
                </div>

                <div className="p-5 space-y-4">
                  {/* Fake input */}
                  <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                    <svg className="w-4 h-4 text-gray-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm text-gray-400 font-mono truncate">hr.jobs@career-opportunities247.com</span>
                  </div>

                  {/* Score card */}
                  <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-bold text-white">Scan Result</span>
                      <span className="flex items-center gap-1.5 text-xs font-bold text-red-400 bg-red-500/10 border border-red-500/30 px-2.5 py-1 rounded-full">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                        High Risk
                      </span>
                    </div>
                    <div className="flex items-end gap-3 mb-3">
                      <span className="text-5xl font-bold text-red-400" style={{ fontFamily: "'Orbitron', sans-serif" }}>8</span>
                      <span className="text-xl text-gray-500 font-bold pb-1">/10</span>
                      <span className="text-xs text-gray-500 pb-1.5 leading-relaxed">AI + DNS + community<br />intelligence</span>
                    </div>
                    <div className="w-full bg-white/5 rounded-full h-2 overflow-hidden">
                      <div className="bg-red-500 h-2 rounded-full" style={{ width: '80%' }} />
                    </div>
                    <div className="flex justify-between text-xs text-gray-600 mt-1">
                      <span>Safe</span><span>Suspicious</span><span>Critical</span>
                    </div>
                  </div>

                  {/* Signals */}
                  <div className="space-y-2">
                    {[
                      { label: 'Recently Created Domain', pts: '+60', color: 'text-red-400' },
                      { label: 'No SPF / DMARC Records', pts: '+25', color: 'text-red-400' },
                      { label: 'Suspicious Domain Extension', pts: '+8', color: 'text-amber-400' },
                    ].map(s => (
                      <div key={s.label} className="flex items-center justify-between text-xs bg-white/3 rounded-lg px-3 py-2 border border-white/5">
                        <span className="text-gray-300">{s.label}</span>
                        <span className={`font-mono font-bold ${s.color}`}>{s.pts}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-center text-xs text-gray-600 mt-2">Example scan — results are generated in under 3 seconds</p>
            </div>

          </section>

          {/* Gradient fade at bottom of hero to blend with next section */}
          <div className="absolute bottom-0 left-0 w-full h-64 bg-gradient-to-t from-[#0a0e17] to-transparent z-20 pointer-events-none" />
        </div>
      </CosmicParallaxBg>

      {/* Steps */}
      <section className="container mx-auto px-4 max-w-5xl pb-20">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="card relative overflow-hidden group hover:border-cyan-500/30 transition-all">
            <div className="absolute top-0 right-0 bg-cyan-600/10 w-24 h-24 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110" />
            <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
              <span className="bg-cyan-600 text-xs rounded px-2 py-1 text-white">STEP 1</span>
              Enter Details
            </h3>
            <p className="text-gray-400">
              Paste the recruiter's email address or phone number, along with the message content for deeper analysis.
            </p>
          </div>

          <div className="card relative overflow-hidden group hover:border-cyan-500/30 transition-all">
            <div className="absolute top-0 right-0 bg-cyan-400/10 w-24 h-24 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110" />
            <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
              <span className="bg-cyan-500 text-xs rounded px-2 py-1 text-white">STEP 2</span>
              Get Verified
            </h3>
            <p className="text-gray-400">
              Receive an instant AI risk assessment, identity verification, and safety recommendation.
            </p>
          </div>

          <div className="card relative overflow-hidden group hover:border-cyan-500/30 transition-all">
            <div className="absolute top-0 right-0 bg-emerald-400/10 w-24 h-24 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110" />
            <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
              <span className="bg-emerald-500 text-xs rounded px-2 py-1 text-white">STEP 3</span>
              Apply with Confidence
            </h3>
            <p className="text-gray-400">
              Move forward knowing the opportunity is real — or walk away before sharing personal information.
            </p>
          </div>
        </div>
      </section>

      {/* Why ScamView */}
      <section className="container mx-auto px-4 max-w-3xl pb-20 text-center">
        <h2 className="text-2xl font-bold text-white mb-4">Why verify with ScamView?</h2>
        <p className="text-gray-400 text-lg mb-8 leading-relaxed">
          Identity theft and recruitment fraud are on the rise. Scammers use sophisticated tactics to impersonate legit companies,
          steal your personal data, and hijack your finances.
        </p>
        <div className="grid md:grid-cols-3 gap-4 text-sm font-medium">
          <div className="bg-red-900/20 py-3 px-4 rounded-lg border border-red-500/20 text-red-300">
            Prevent Identity Theft
          </div>
          <div className="bg-amber-900/20 py-3 px-4 rounded-lg border border-amber-500/20 text-amber-300">
            Avoid Financial Loss
          </div>
          <div className="bg-cyan-900/20 py-3 px-4 rounded-lg border border-cyan-500/20 text-cyan-300">
            Protect Your Data
          </div>
        </div>
      </section>

      {/* PAS Section */}
      <section className="bg-[#0d1321] border-y border-white/5 py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-3xl md:text-5xl font-bold text-white mb-4"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              Stop Worrying. Start Applying.
            </h2>
            <p className="text-xl text-cyan-400 font-semibold">
              Apply with confidence. Let ScamView filter out the fakes.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <div className="card hover:border-red-500/30 transition-colors">
              <div className="w-10 h-10 bg-red-500/15 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h4 className="text-xl font-bold text-white mb-2">The Problem</h4>
              <p className="text-gray-400">
                You finally found a job that feels like the perfect fit. But is the recruiter real?
                Is the offer a trap? You shouldn't have to be a detective.
              </p>
            </div>
            <div className="card hover:border-amber-500/30 transition-colors">
              <div className="w-10 h-10 bg-amber-500/15 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h4 className="text-xl font-bold text-white mb-2">The Threat</h4>
              <p className="text-gray-400">
                Scammers are getting smarter, using AI to create "perfect" job postings and fake
                profiles to steal your data and your peace of mind.
              </p>
            </div>
            <div className="card bg-cyan-900/10 border-cyan-500/20 hover:border-cyan-400/40 transition-colors">
              <div className="w-10 h-10 bg-cyan-500/15 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h4 className="text-xl font-bold text-white mb-2">The Solution</h4>
              <p className="text-gray-300">
                ScamView is your personal security layer. In seconds, we analyze recruiters and
                job offers, giving you a clear, actionable Risk Score.
              </p>
            </div>
          </div>

          {/* Trust Signals */}
          <h2 className="text-3xl font-bold text-center text-white mb-12">
            Why Job Seekers Trust ScamView
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            <div className="card">
              <div className="w-10 h-10 bg-yellow-500/15 rounded-lg flex items-center justify-center mb-3">
                <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h4 className="font-bold text-white mb-2">Instant Risk Scoring</h4>
              <p className="text-sm text-gray-400">Know immediately if a recruiter or role is high-risk.</p>
            </div>
            <div className="card">
              <div className="w-10 h-10 bg-cyan-500/15 rounded-lg flex items-center justify-center mb-3">
                <svg className="w-5 h-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11l2 2 4-4" />
                </svg>
              </div>
              <h4 className="font-bold text-white mb-2">Recruiter Verification</h4>
              <p className="text-sm text-gray-400">We cross-check data to ensure you're talking to a human, not a bot.</p>
            </div>
            <div className="card">
              <div className="w-10 h-10 bg-emerald-500/15 rounded-lg flex items-center justify-center mb-3">
                <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h4 className="font-bold text-white mb-2">Data Privacy</h4>
              <p className="text-sm text-gray-400">Keep your sensitive info out of the hands of identity thieves.</p>
            </div>
            <div className="card">
              <div className="w-10 h-10 bg-purple-500/15 rounded-lg flex items-center justify-center mb-3">
                <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="font-bold text-white mb-2">Time Savings</h4>
              <p className="text-sm text-gray-400">Stop wasting hours on interviews that lead to nowhere.</p>
            </div>
          </div>

          {/* Featured Testimonial */}
          <div className="bg-gradient-to-r from-cyan-900/20 to-cyan-800/10 p-8 md:p-12 rounded-2xl border border-cyan-500/20 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-10 text-9xl font-serif text-white">"</div>
            <p className="text-xl md:text-2xl italic text-gray-200 mb-8 relative z-10 max-w-3xl mx-auto">
              "I spent weeks interviewing for a role that turned out to be a scam. ScamView would have caught it in five seconds.
              <span className="text-cyan-400 font-bold"> Now, I don't apply without it.</span>"
            </p>
            <div>
              <div className="font-bold text-white">Sarah J.</div>
              <div className="text-sm text-gray-500">Graphic Designer</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="container mx-auto px-4 py-20 max-w-6xl">
        <h2 className="text-3xl font-bold text-center text-white mb-12">
          How ScamView Protects You
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="card text-center">
            <div className="w-12 h-12 bg-cyan-500/15 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-3">AI Analysis</h3>
            <p className="text-gray-300">
              AI-powered detection identifies urgency tactics, pressure patterns,
              and financial red flags in messages.
            </p>
          </div>
          <div className="card text-center">
            <div className="w-12 h-12 bg-cyan-500/15 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Technical Verification</h3>
            <p className="text-gray-300">
              DNS authentication, domain age checks, and VOIP detection
              verify sender legitimacy.
            </p>
          </div>
          <div className="card text-center">
            <div className="w-12 h-12 bg-cyan-500/15 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Community Intelligence</h3>
            <p className="text-gray-300">
              Real-time web search cross-references scam reports and
              community warnings.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="container mx-auto px-4 py-20 max-w-6xl">
        <h2 className="text-3xl font-bold text-center text-white mb-12">
          Trusted by job seekers everywhere
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          <TestimonialCard
            quote="I almost lost $2,000 to a fake equipment check scam. ScamView flagged it instantly."
            author="Sarah J."
            role="Graphic Designer"
          />
          <TestimonialCard
            quote="Recruiters were ghosting me. Turns out half weren't real. This tool saved my sanity."
            author="Mike T."
            role="Software Engineer"
          />
          <TestimonialCard
            quote="Simple, fast, and thorough. The AI caught red flags in a recruiter email I almost fell for."
            author="Jessica L."
            role="Marketing Manager"
          />
        </div>
      </section>

      {/* Stats */}
      <section className="container mx-auto px-4 py-10 max-w-6xl">
        <div className="card bg-white/5">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-white mb-2">10+</div>
              <div className="text-gray-400">Detection Signals</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">&lt;60s</div>
              <div className="text-gray-400">Typical Scan Time</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">Free</div>
              <div className="text-gray-400">Preview Before You Sign Up</div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="container mx-auto px-4 py-20 max-w-4xl text-center">
        <h2
          className="text-3xl font-bold text-white mb-4"
          style={{ fontFamily: "'Orbitron', sans-serif" }}
        >
          Reclaim Your Peace of Mind
        </h2>
        <p className="text-gray-400 mb-8 max-w-2xl mx-auto text-lg">
          Don't let a fake recruiter steal your future. Use ScamView to stay safe,
          stay focused, and get hired faster.
        </p>
        <Link
          to="/free-scan"
          className="inline-block bg-white text-black text-lg px-8 py-4 rounded-full font-bold hover:bg-gray-200 transition-all shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95"
        >
          Get Your First Risk Score Free
        </Link>
        <p className="text-xs text-gray-500 mt-4">Includes a 14-day Premium trial. No credit card required for free scan.</p>
      </section>

      <Footer />
    </div>
  )
}
